import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  OptionProps,
  MultiValueProps,
  MenuProps,
} from "react-select";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";

// Define interface for policy value
interface PolicyValue {
  id: number;
  Policy_id: string;
  Policy_name: string;
  plans?: any[]; // Plans array (optional for backward compatibility)
}

interface PolicyOption {
  value: PolicyValue;
  label: string;
}

interface PolicyNamesProps {
  // Keep flexible because this shared component is used by many old pages
  // with different handler signatures: Option[], PolicyValue[], Option | Option[] | null.
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any[];
  qaIdPrefix?: string;

  // Some existing pages pass isMulti={false}. Keeping it here avoids TS excess-prop errors.
  // The Select remains multi-select to preserve the original component behavior.
  isMulti?: boolean;

  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "policy-names";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const PolicyNames = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: PolicyNamesProps) => {
  const [options, setOptions] = useState<PolicyOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<PolicyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await PolicyAxiosInstance.get("searchpolicy", {
        params: { policy_name: inputValue || "", active: true },
      });
      const data = response.data?.result || [];
      console.log("data: ------------", data);
      const formattedOptions: PolicyOption[] = data.map((item: any) => {
        const planArr = Array.isArray(item.plan) ? item.plan : [];
        const planNames = planArr
          .map((p: any) => (p?.Plan_name || p?.plan_name || p?.name || "").trim())
          .filter((n: string) => n)
          .join(", ");
        const planSuffix = planNames ? ` (${planNames})` : "";

        return {
          value: {
            id: item.id, // Database ID (not reliable for matching)
            Policy_id: item.policy_id || item.Policy_id,
            Policy_name: item.policy_name || item.Policy_name,
            plans: planArr, // ✅ Include complete plans array
          },
          label: `(${item.policy_id || item.Policy_id}) ${item.policy_name || item.Policy_name}${planSuffix}`,
        };
      });

      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading policy options:", error);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadOptions();
    }, 300);
    return () => clearTimeout(debounce);
  }, [inputValue, loadOptions]);

  const handleChange = (selected: MultiValue<PolicyOption>) => {
    const selectedArray = selected as PolicyOption[];
    setSelectedOptions(selectedArray);

    // Existing behavior preserved: pass value objects to parent.
    const values = selectedArray.map((option) => option.value);
    onChange(values);
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  useEffect(() => {
    if (defaultValue) {
      // Convert defaultValue to PolicyOption format
      const formattedDefault = Array.isArray(defaultValue)
        ? defaultValue.map((item: any) => {
            // Check if item is in the new format
            console.log("item: ------------", item);
            if (item.value && item.value.Policy_id) {
              const plans = item.value.plans || [];
              const planNames = plans.map((p: any) => p.Plan_name || p.plan_name || p.name || "").join(", ");
              return {
                value: item.value,
                label:
                  item.label ||
                  `(${item.value.Policy_id}) ${item.value.Policy_name}${planNames ? ` (${planNames})` : ""}`,
              };
            }
            // Check if item is in old format
            else if (item.Policy_id || item.policy_id) {
              const id = item.id || 0;
              const policyId = item.Policy_id || item.policy_id || "";
              const policyName = item.Policy_name || item.policy_name || "";
              const plans = item.plans || item.plan || [];
              const planNames = plans.map((p: any) => p.Plan_name || p.plan_name || p.name || "").join(", ");
              return {
                value: {
                  id: id,
                  Policy_id: policyId,
                  Policy_name: policyName,
                  plans: plans,
                },
                label: `(${policyId}) ${policyName}${planNames ? ` (${planNames})` : ""}`,
              };
            }
            // Fallback for old format with value/label
            else {
              return {
                value: {
                  id: 0,
                  Policy_id: item.value || "",
                  Policy_name: item.label || "",
                },
                label: item.label || item.value || "",
              };
            }
          })
        : [];
      setSelectedOptions(formattedDefault);
    }
  }, [defaultValue]);

  const customStyles: StylesConfig<PolicyOption, true> = {
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#007BFF",
      color: "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: "#FFFFFF",
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
  };

  const Menu = (props: MenuProps<PolicyOption, true>) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (props: OptionProps<PolicyOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `option-${qaSafe(props.data.value.Policy_id || props.data.value.id)}`)}
      data-option-label={props.data.label}
      data-policy-db-id={props.data.value.id}
      data-policy-id={props.data.value.Policy_id}
      data-policy-name={props.data.value.Policy_name}
      data-policy-plans-count={props.data.value.plans?.length || 0}
    >
      <components.Option {...props} />
    </div>
  );

  const MultiValueComponent = (props: MultiValueProps<PolicyOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(props.data.value.Policy_id || props.data.value.id)}`)}
      data-selected-label={props.data.label}
      data-policy-db-id={props.data.value.id}
      data-policy-id={props.data.value.Policy_id}
      data-policy-name={props.data.value.Policy_name}
      data-policy-plans-count={props.data.value.plans?.length || 0}
    >
      <components.MultiValue {...props} />
    </div>
  );

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        aria-label="Policy selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={setInputValue}
        placeholder="Type at least 3 letters to search..."
        styles={customStyles}
        className={`w-100 mb-3 ${selectQaId}`}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value.id.toString()}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>
    </div>
  );
};

export default PolicyNames;
